import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "context/NotificationContext";

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const notificationTypeConfig = {
  convert_to_order: {
    icon: "📋",
    color: "#0066cc",
    label: "Order Conversion",
  },
  order_cancelled: {
    icon: "✖️",
    color: "#dc3545",
    label: "Order Cancelled",
  },
  idle_quotation: {
    icon: "🔔",
    color: "#ff9800",
    label: "Idle Quotation",
  },
  order_delivered: {
    icon: "✓",
    color: "#28a745",
    label: "Order Delivered",
  },
};

function NotificationItem({ notification, onClose }) {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();
  const typeConfig = notificationTypeConfig[notification.type] || {
    icon: "🔔",
    color: "#666",
    label: notification.type,
  };

  const handleNotificationClick = async () => {
    // Mark as read
    if (!notification.isRead) {
      await markAsRead(notification.id, true);
    }

    // Navigate to order/quotation details
    if (notification.orderId) {
      navigate(`/quotation/${notification.orderId}`);
      if (onClose) onClose();
    }
  };

  const handleMarkAsRead = async (e) => {
    e.stopPropagation();
    await markAsRead(notification.id, !notification.isRead);
  };

  return (
    <Box
      onClick={handleNotificationClick}
      sx={{
        p: 2,
        borderBottom: "1px solid #eee",
        cursor: "pointer",
        backgroundColor: notification.isRead ? "#ffffff" : "#f9f9f9",
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: notification.isRead ? "#f5f5f5" : "#f0f0f0",
        },
      }}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ fontSize: 20, minWidth: 24 }}>{typeConfig.icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.isRead ? 400 : 600,
                color: "#333",
              }}
            >
              {notification.message}
            </Typography>
            <Tooltip title={notification.isRead ? "Mark unread" : "Mark read"}>
              <Button
                size="small"
                sx={{
                  minWidth: "auto",
                  padding: "4px 8px",
                  color: "#999",
                  "&:hover": { color: "#333" },
                }}
                onClick={handleMarkAsRead}
              >
                {notification.isRead ? "✓" : "○"}
              </Button>
            </Tooltip>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: "#999",
              display: "block",
              mt: 0.5,
            }}
          >
            {formatTimeAgo(notification.createdAt)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    isRead: PropTypes.number.isRequired,
    createdAt: PropTypes.string.isRequired,
    orderId: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func,
};

NotificationItem.defaultProps = {
  onClose: null,
};

export default NotificationItem;
